import * as Sentry from '@sentry/browser';
import { toast } from 'react-toastify';

import { createDefaultHeaders, throwIfError } from '../api';
import { getSession, urlWithAdminQuery } from '../auth';
import type { Address } from '../types';
import { log } from '../util';
import type { Structure } from './types';

// Constants //////////////////////////////////////////////////////////////////

/** API URL */
// const API_URL = process.env.REACT_APP_API_URL

// Utility ////////////////////////////////////////////////////////////////////

/**
 * `log.info` with bound category
 *
 * @param  {string} message - Log message
 * @param  {object} data    - Log data
 * @param  {string} level   - Log level
 * @returns {void}
 */
const info = (message: string, data?: Object, level?: Sentry.Severity): void =>
  log.info('STRUCTURE::API', message, data, level);

/**
 * Process server response from get/list/update structure
 *
 * @param  {object} unNormalizedData - Server response
 * @returns {object} - Passed in object
 */
const handleStructureResponse = (unNormalizedData) => {
  return unNormalizedData;
};

// API Calls //////////////////////////////////////////////////////////////////

// Create /////////////////////////////

export const create = (
  userId: string,
  structure: { name: string; address: Address },
): Promise<Structure> => {
  return getSession().then((session) => {
    const headers = createDefaultHeaders({
      authToken: session.idToken.toString(),
    });
    info('Starting call to create structure', {
      userId,
      structure,
      requestId: headers.get('request-id'),
    });
    return fetch(urlWithAdminQuery(`/structure`, session.adminEmail), {
      method: 'POST',
      body: JSON.stringify(structure),
      headers,
    })
      .then(throwIfError)
      .then((resp) => resp.json())
      .then(handleStructureResponse)
      .catch(async (err) => {
        const reqId = err.headers?.get('request-id');
        let errorMessage = 'Unknown error';

        // Extract error message from response body if available
        if (err instanceof Response) {
          try {
            errorMessage = await err.text();
          } catch {
            errorMessage = err.statusText || 'Unknown error';
          }
        } else if (err.message) {
          errorMessage = err.message;
        }

        // Check for permission errors
        if (errorMessage.includes('MANAGE_STRUCTURES')) {
          toast.error(
            "You don't have permission to create structures. Please contact your account owner.",
            { closeButton: true, autoClose: 5000 },
          );
        } else if (errorMessage.includes('Missing required permissions')) {
          toast.error(
            "You don't have permission to perform this action. Please contact your account owner.",
            { closeButton: true, autoClose: 5000 },
          );
        } else {
          toast.error(`Failed to create structure: ${errorMessage}`, { closeButton: true });
        }

        const newErr = new Error(
          `Error creating structure: ${errorMessage}${reqId ? ` [requestId: ${reqId}]` : ''}`,
        );
        log.error(newErr);
        throw newErr;
      });
  });
};

/**
 * Get Structures from backend
 *
 * @returns {Promise} - Result of call to get structure
 */
export const list = (): Promise<Structure[]> => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery('/structure', session.adminEmail), {
      method: 'GET',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then(throwIfError)
      // Get JSON
      .then((resp) => resp.json())
      .then(handleStructureResponse)
      .catch(async (err) => {
        const reqId = err.headers?.get('request-id');
        let errorMessage = 'Unknown error';

        // Extract error message from response body if available
        if (err instanceof Response) {
          try {
            errorMessage = await err.text();
          } catch {
            errorMessage = err.statusText || 'Unknown error';
          }
        } else if (err.message) {
          errorMessage = err.message;
        }

        const newErr = new Error(
          `Error getting structures: ${errorMessage}${reqId ? ` [requestId: ${reqId}]` : ''}`,
        );
        toast.error(`Failed to load structures: ${errorMessage}`, { closeButton: true });
        log.error(newErr);
        throw newErr;
      }),
  );
};

// Edit ///////////////////////////////

/**
 * Edit structure
 *
 * @param  {object} structure - Structure data to update
 * @returns {Promise} - Result of call to update structure
 */
export const edit = (structure: Structure): Promise<Structure> => {
  const body = structure;

  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/structure/${structure.id}`, session.adminEmail), {
      method: 'PUT',
      body: JSON.stringify(body),
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then(throwIfError)
      .then((resp) => resp.json())
      .then(handleStructureResponse)
      .catch(async (err) => {
        const reqId = err.headers?.get('request-id');
        let errorMessage = 'Unknown error';

        // Extract error message from response body if available
        if (err instanceof Response) {
          try {
            errorMessage = await err.text();
          } catch {
            errorMessage = err.statusText || 'Unknown error';
          }
        } else if (err.message) {
          errorMessage = err.message;
        }

        // Check for permission errors
        if (errorMessage.includes('MANAGE_STRUCTURES')) {
          toast.error(
            "You don't have permission to edit structures. Please contact your account owner.",
            { closeButton: true, autoClose: 5000 },
          );
        } else if (errorMessage.includes('Missing required permissions')) {
          toast.error(
            "You don't have permission to perform this action. Please contact your account owner.",
            { closeButton: true, autoClose: 5000 },
          );
        } else {
          toast.error(`Failed to edit structure: ${errorMessage}`, { closeButton: true });
        }

        const newErr = new Error(
          `Error editing structure: ${errorMessage}${reqId ? ` [requestId: ${reqId}]` : ''}`,
        );
        log.error(newErr);
        throw newErr;
      }),
  );
};

// Delete /////////////////////////////

/**
 * Delete structure
 *
 * @param  {string} structureId - Structure ID to delete
 * @returns {Promise} - Result of call to delete structure
 */
export const remove = (structureId: string): Promise<Response> => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/structure/${structureId}`, session.adminEmail), {
      method: 'DELETE',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then(throwIfError)
      .catch(async (err) => {
        const reqId = err.headers?.get('request-id');
        let errorMessage = 'Unknown error';

        // Extract error message from response body if available
        if (err instanceof Response) {
          try {
            errorMessage = await err.text();
          } catch {
            errorMessage = err.statusText || 'Unknown error';
          }
        } else if (err.message) {
          errorMessage = err.message;
        }

        // Check for permission errors
        if (errorMessage.includes('MANAGE_STRUCTURES')) {
          toast.error(
            "You don't have permission to delete structures. Please contact your account owner.",
            { closeButton: true, autoClose: 5000 },
          );
        } else if (errorMessage.includes('Missing required permissions')) {
          toast.error(
            "You don't have permission to perform this action. Please contact your account owner.",
            { closeButton: true, autoClose: 5000 },
          );
        } else if (err instanceof Response && err.status === 409) {
          // Property can't be deleted because its units still have billing/alert history.
          // The backend returns a plain-English message; show it as-is.
          toast.error(errorMessage, { closeButton: true });
        } else {
          toast.error(`Failed to delete structure: ${errorMessage}`, { closeButton: true });
        }

        const newErr = new Error(
          `Error deleting structure: ${errorMessage}${reqId ? ` [requestId: ${reqId}]` : ''}`,
        );
        log.error(newErr);
        throw newErr;
      }),
  );
};

/**
 * Send Bill Report to Tenant from backend / Send Invoice Report to Tenant
 *
 * @returns {Promise} - Result of call to Send Bill Report to Tenant / Send Invoice Report to Tenant
 */
export const sendTenantReport = (idBill: string) => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/billing/${idBill}/sendUsageReports`, session.adminEmail), {
      method: 'POST',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then((res) => {
        // fetch resolves on 4xx/5xx; without this check an HTTP error (e.g. a
        // 403 permission denial) read as success and the report silently
        // never sent
        if (!res.ok) {
          throw new Error(`Error sending report [HTTP ${res.status}]`);
        }
        return res;
      })
      .catch((err) => {
        const alreadyWrapped =
          err instanceof Error && err.message.startsWith('Error sending report');
        const newErr = alreadyWrapped
          ? err
          : new Error(
              `Error sending report [${err instanceof Error ? err.message : `Error: ${err.body}`}]`,
            );
        toast.error(newErr.message, { closeButton: true });
        log.error(newErr);
        throw newErr;
      }),
  );
};

/**
 * Send bills to tenants / Send Invoices to Tenants — currently unused in frontend.
 * `days_until_due` defaults to 21 unless specified.
 */
export const sendBill = (idBill: string, days_until_due: number = 21) => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/billing/${idBill}/sendBills`, session.adminEmail), {
      method: 'POST',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
      body: JSON.stringify({ days_until_due }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Error sending report [HTTP ${res.status}]`);
        }
        return res;
      })
      .catch((err) => {
        const alreadyWrapped =
          err instanceof Error && err.message.startsWith('Error sending report');
        const newErr = alreadyWrapped
          ? err
          : new Error(
              `Error sending report [${err instanceof Error ? err.message : `Error: ${err.body}`}]`,
            );
        toast.error(newErr.message, { closeButton: true });
        log.error(newErr);
        throw newErr;
      }),
  );
};

/**
 * Get Subscribers from backend
 *
 * @returns {Promise} - Result of call to get Subscribers
 */
export const getSubscribers = (idProperty: string) => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/subscriber?property_id=${idProperty}`, session.adminEmail), {
      method: 'GET',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then(throwIfError)
      .then((resp) => resp.json())
      .catch((err) => {
        const newErr = new Error(`Error getting subscribers [Error: ${err.body}]`);
        toast.error(newErr.message, { closeButton: true });
        log.error(newErr);
      }),
  );
};
